import React from 'react';
import { createTimeInstance } from '@utils/time';
import { listSpecs } from '@services/scoreSpecs';
import { specDisplayName } from './format';
import {
  EMPTY_LIST_FILTERS,
  matchesAuthor,
  matchesQuery,
  matchesUpdatedRange,
  resolveFilterRange,
  updatedAtOf,
} from './listFilters';

const ALL = 'all';

// A spec with BOTH timestamps explicitly null sorts last rather than throwing: createTimeInstance
// returns null for null (utils/time.js:9), so calling .valueOf() on it is a TypeError, and a record
// in that shape is not hypothetical.
const updatedTs = (spec) => {
  const value = updatedAtOf(spec);
  if (!value) return 0;
  const instance = createTimeInstance(value);
  return instance && instance.isValid() ? instance.valueOf() : 0;
};

// Owns the authoring-list state: server-side status/source filters (re-fetch), client-side text,
// author and updated-window filters, and most-recently-updated sort. Mirrors the local-state
// approach in ScoreBackfill (no Redux slice for the feature's own data).
//
// The split is not a style choice: the list endpoint accepts `status` and `source` and nothing else
// (services/scoreSpecs.js), so everything else has to be a predicate over what came back. `reload`
// therefore keys off those two ONLY — widening its dependency array is what would turn a keystroke
// or a date pick into a network request.
//
// Two screens share this hook: the advanced list and scoreSpecFetch/FetchSpecForm. The filters are
// the hook's own state unless the caller passes a `filters` / `setFilters(patch)` pair of its own,
// in which case the hook reads and writes those and owns nothing. The list does that — its filters
// live in the URL (useListFilterUrl) — and the fetch screen does not, so its status switch is plain
// state and the author/range filters sit at their pass-through defaults.
//
// `currentUserId` is taken as an argument rather than read from the store here, so the hook stays
// ignorant of redux and the fetch screen — which never filters by author — passes nothing.
export const useScoreSpecs = ({
  currentUserId = null,
  filters: ownedFilters = null,
  setFilters: setOwnedFilters = null,
} = {}) => {
  const [specs, setSpecs] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [localFilters, setLocalFilters] = React.useState(EMPTY_LIST_FILTERS);

  const patchLocalFilters = React.useCallback((patch) => setLocalFilters((prev) => ({ ...prev, ...patch })), []);

  const filters = ownedFilters || localFilters;
  const setFilters = ownedFilters ? setOwnedFilters : patchLocalFilters;

  const reload = React.useCallback(async () => {
    setError(null);
    const { specs: list, error: loadError } = await listSpecs({
      status: filters.status === ALL ? undefined : filters.status,
      source: filters.source === ALL ? undefined : filters.source,
    });
    setSpecs(list || []);
    setError(loadError);
  }, [filters.status, filters.source]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const { q, author, rangePreset, rangeFrom, rangeTo } = filters;

  const visible = React.useMemo(() => {
    const query = (q || '').trim().toLowerCase();
    const resolvedRange = resolveFilterRange({ rangePreset, rangeFrom, rangeTo });
    return (specs || [])
      .filter(
        (s) =>
          matchesQuery(s, query, specDisplayName(s)) &&
          matchesAuthor(s, author, currentUserId) &&
          matchesUpdatedRange(s, resolvedRange)
      )
      .sort((a, b) => updatedTs(b) - updatedTs(a));
  }, [specs, q, author, currentUserId, rangePreset, rangeFrom, rangeTo]);

  return { specs, visible, isLoading: specs === null, error, filters, setFilters, reload };
};
